import type {LabelPickerLabel} from '@github-ui/item-picker-label'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  updateLabelsForLabelableRelayMutation,
  updateLabelsForLabelableRelayMutation$data,
} from './__generated__/updateLabelsForLabelableRelayMutation.graphql'

/**
 * Computes the diff between initial and selected labels
 */
export function computeLabelDiff(
  initialLabels: LabelPickerLabel[],
  selectedLabels: LabelPickerLabel[],
): {labelsToAdd: LabelPickerLabel[]; labelsToRemove: LabelPickerLabel[]} {
  const initialIds = new Set(initialLabels.map(l => l.id))
  const selectedIds = new Set(selectedLabels.map(l => l.id))

  const labelsToAdd = selectedLabels.filter(label => !initialIds.has(label.id))
  const labelsToRemove = initialLabels.filter(label => !selectedIds.has(label.id))

  return {labelsToAdd, labelsToRemove}
}

/**
 * Updates labels on a labelable (Issue or PullRequest) using diff-based approach.
 * This mutation is safer for concurrent edits as it only adds/removes specific labels
 * rather than replacing the entire label set.
 */
export function commitUpdateLabelsForLabelableMutation({
  environment,
  input: {labelableId, labelsToAdd, labelsToRemove, labelableTypeName},
  clientMutationId,
  onError,
  onCompleted,
  expectedFinalLabels,
}: {
  environment: Environment
  input: {
    labelableId: string
    labelsToAdd: LabelPickerLabel[]
    labelsToRemove: LabelPickerLabel[]
    labelableTypeName: 'Issue' | 'PullRequest'
  }
  clientMutationId?: string
  onError?: (error: Error) => void
  onCompleted?: (response: updateLabelsForLabelableRelayMutation$data) => void
  /** Expected final labels after the mutation, used for optimistic response */
  expectedFinalLabels: LabelPickerLabel[]
}) {
  return commitMutation<updateLabelsForLabelableRelayMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation updateLabelsForLabelableRelayMutation($input: UpdateLabelsForLabelableInput!) @raw_response_type {
        updateLabelsForLabelable(input: $input) {
          labelable {
            ...LabelsSectionAssignedLabels
          }
        }
      }
    `,
    variables: {
      input: {
        labelableId,
        addLabelIds: labelsToAdd.map(l => l.id),
        removeLabelIds: labelsToRemove.map(l => l.id),
        clientMutationId,
      },
    },
    optimisticResponse: {
      updateLabelsForLabelable: {
        labelable: {
          __typename: labelableTypeName,
          __isNode: 'true',
          __isLabelable: 'true',
          id: labelableId,
          labels: {
            pageInfo: {
              hasNextPage: false,
              endCursor: null,
            },
            edges: expectedFinalLabels.map(a => ({
              cursor: a.id,
              node: {
                __typename: 'Label',
                id: a.id,
                name: a.name,
                nameHTML: a.nameHTML,
                color: a.color,
                url: a.url,
                description: a.description,
              },
            })),
          },
        },
      },
    },
    onError: error => onError && onError(error),
    onCompleted: response => onCompleted && onCompleted(response),
  })
}
